/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

import { validateRequired, validatePhone } from './validation';
import {
  StudioFormData,
  StudioFormErrors,
  StudioFormValidationRules,
} from '../types/studioForms';

// Studio form validation rules
export const STUDIO_VALIDATION_RULES: StudioFormValidationRules = {
  name: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  description: {
    maxLength: 500,
  },
  email: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  },
  website: {
    pattern: /^https?:\/\/.+\..+/,
  },
  phone: {
    pattern: /^\+?[\d\s-()]+$/,
  },
  logo: {
    maxSize: 5 * 1024 * 1024, // 5MB
    allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  },
};

/**
 * Validates studio name field.
 * @param name - The studio name to validate
 * @returns Error message if invalid, undefined if valid
 */
export const validateStudioName = (name: string): string | undefined => {
  const requiredError = validateRequired(name, 'Studio name');
  if (requiredError) return requiredError;

  if (name.length < STUDIO_VALIDATION_RULES.name.minLength) {
    return `Studio name must be at least ${STUDIO_VALIDATION_RULES.name.minLength} characters`;
  }

  if (name.length > STUDIO_VALIDATION_RULES.name.maxLength) {
    return `Studio name must be less than ${STUDIO_VALIDATION_RULES.name.maxLength} characters`;
  }

  return undefined;
};

/**
 * Validates studio description field.
 * @param description - The description to validate
 * @returns Error message if invalid, undefined if valid
 */
export const validateStudioDescription = (
  description: string
): string | undefined => {
  // Description is optional
  if (!description || !description.trim()) {
    return undefined;
  }

  if (description.length > STUDIO_VALIDATION_RULES.description.maxLength) {
    return `Description must be less than ${STUDIO_VALIDATION_RULES.description.maxLength} characters`;
  }

  return undefined;
};

/**
 * Validates studio email field.
 * @param email - The email to validate
 * @returns Error message if invalid, undefined if valid
 */
export const validateStudioEmail = (email: string): string | undefined => {
  const requiredError = validateRequired(email, 'Studio email');
  if (requiredError) return requiredError;

  if (!STUDIO_VALIDATION_RULES.email.pattern.test(email)) {
    return 'Please enter a valid email address';
  }

  return undefined;
};

/**
 * Validates website URL field.
 * @param website - The website URL to validate
 * @returns Error message if invalid, undefined if valid
 */
export const validateWebsite = (website: string): string | undefined => {
  // Website is optional
  if (!website || !website.trim()) {
    return undefined;
  }

  if (!STUDIO_VALIDATION_RULES.website.pattern.test(website)) {
    return 'Please enter a valid website URL (e.g., https://example.com)';
  }

  return undefined;
};

/**
 * Validates business address field.
 * @param address - The address to validate
 * @returns Error message if invalid, undefined if valid
 */
export const validateAddress = (address: string): string | undefined => {
  // Address is optional
  if (!address || !address.trim()) {
    return undefined;
  }

  if (address.length > 200) {
    return 'Address must be less than 200 characters';
  }

  return undefined;
};

/**
 * Validates logo file upload.
 * @param file - The file to validate
 * @returns Error message if invalid, undefined if valid
 */
export const validateLogoFile = (file: File | null): string | undefined => {
  // Logo is optional
  if (!file) {
    return undefined;
  }

  // Check file size
  if (file.size > STUDIO_VALIDATION_RULES.logo.maxSize) {
    const maxSizeMB = STUDIO_VALIDATION_RULES.logo.maxSize / (1024 * 1024);
    return `Logo file must be smaller than ${maxSizeMB}MB`;
  }

  // Check file type
  if (!STUDIO_VALIDATION_RULES.logo.allowedTypes.includes(file.type)) {
    return 'Logo must be an image file (JPEG, PNG, GIF, or WebP)';
  }

  return undefined;
};

/**
 * Validates join request message field.
 * @param message - The join request message to validate
 * @returns Error message if invalid, undefined if valid
 */
export const validateJoinRequestMessage = (
  message: string
): string | undefined => {
  // Join request message is optional
  if (!message || !message.trim()) {
    return undefined;
  }

  if (message.length > 500) {
    return 'Join request message must be less than 500 characters';
  }

  return undefined;
};

/**
 * Validates an entire studio form.
 * @param formData - The form data to validate
 * @returns Object containing validation errors
 */
export const validateStudioForm = (
  formData: StudioFormData
): StudioFormErrors => {
  const errors: StudioFormErrors = {};

  // Validate required fields
  const nameError = validateStudioName(formData.name);
  if (nameError) errors.name = nameError;

  // Email validation removed - email is automatically populated from authenticated user

  // Validate optional fields
  const descriptionError = validateStudioDescription(formData.description);
  if (descriptionError) errors.description = descriptionError;

  const websiteError = validateWebsite(formData.businessInfo.website || '');
  if (websiteError) errors.website = websiteError;

  const phoneError = validatePhone(formData.businessInfo.phone || '');
  if (phoneError) errors.phone = phoneError;

  const addressError = validateAddress(formData.businessInfo.address || '');
  if (addressError) errors.address = addressError;

  const logoError = validateLogoFile(formData.logo || null);
  if (logoError) errors.logo = logoError;

  const joinRequestMessageError = validateJoinRequestMessage(
    formData.joinRequestMessage || ''
  );
  if (joinRequestMessageError)
    errors.joinRequestMessage = joinRequestMessageError;

  return errors;
};

/**
 * Checks if a studio form has any validation errors.
 * @param errors - The validation errors object
 * @returns True if form is valid (no errors), false otherwise
 */
export const isStudioFormValid = (errors: StudioFormErrors): boolean =>
  Object.keys(errors).length === 0;

/**
 * Validates a single field in the studio form.
 * @param field - The field name to validate
 * @param value - The field value
 * @param formData - The complete form data (for context)
 * @returns Error message if invalid, undefined if valid
 */
export const validateStudioFormField = (
  field: keyof StudioFormData,
  value: any,
  _formData: StudioFormData
): string | undefined => {
  switch (field) {
    case 'name':
      return validateStudioName(value);
    case 'description':
      return validateStudioDescription(value);
    case 'email':
      return validateStudioEmail(value);
    case 'logo':
      return validateLogoFile(value);
    default:
      // For nested businessInfo fields
      if (field === 'businessInfo') {
        return undefined; // Validate individual business info fields separately
      }
      return undefined;
  }
};

/**
 * Validates business info fields specifically.
 * @param field - The business info field name
 * @param value - The field value
 * @returns Error message if invalid, undefined if valid
 */
export const validateBusinessInfoField = (
  field: 'address' | 'phone' | 'website',
  value: string
): string | undefined => {
  switch (field) {
    case 'address':
      return validateAddress(value);
    case 'phone':
      return validatePhone(value);
    case 'website':
      return validateWebsite(value);
    default:
      return undefined;
  }
};

/**
 * Creates initial form data for studio creation.
 * @returns Initial form data object
 */
export const createInitialStudioFormData = (): StudioFormData => ({
  name: '',
  description: '',
  // email is omitted - will be populated from authenticated user
  logo: null,
  businessInfo: {
    address: '',
    phone: '',
    website: '',
  },
  timezone: '',
  studioType: undefined,
  allowJoinRequests: false,
  joinRequestMessage: '',
});

/**
 * Creates form data from existing studio for editing.
 * @param studio - The studio to create form data from
 * @returns Form data populated with studio information
 */
export const createStudioFormDataFromStudio = (
  studio: any
): StudioFormData => ({
  name: studio.name || '',
  description: studio.description || '',
  email: studio.email || '',
  logo: null, // Logo file will be handled separately
  businessInfo: {
    // Handle address object structure from backend
    address:
      studio.address?.street_address ||
      studio.businessInfo?.address ||
      (typeof studio.address === 'string' ? studio.address : '') ||
      '',
    phone: studio.businessInfo?.phone || studio.phone || '',
    website: studio.businessInfo?.website || studio.website || '',
  },
  timezone: studio.timezone || '',
  studioType: studio.studioType || studio.studio_type || undefined,
  allowJoinRequests: studio.allow_join_requests || false,
  joinRequestMessage: studio.join_request_message || '',
});
